import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { Response } from "./Response";
import SharedPreferences from "../../core/utils/SharedPreferences";
import { STORAGE_KEYS } from "../constant/Constants";
import { AesGcmEncryptionService } from "../service/encryptionService";

interface ApiEndpoint {
  url: string;
  method: "get" | "post" | "put" | "delete";
}

type Encryptor =
  | AesGcmEncryptionService
  | (() => Promise<AesGcmEncryptionService>);

function canonicalizePath(input: string): string {
  if (!input) return "/";
  let path = input;
  try {
    if (/^https?:\/\//i.test(path)) {
      path = new URL(path).pathname;
    }
  } catch {
    // ignore parsing errors
  }
  const q = path.indexOf("?");
  if (q >= 0) path = path.substring(0, q);
  const h = path.indexOf("#");
  if (h >= 0) path = path.substring(0, h);
  if (!path.startsWith("/")) path = `/${path}`;
  if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
  return path;
}

export class ApiService {
  constructor(
    private readonly axiosInstance: AxiosInstance,
    private readonly encryption?: Encryptor
  ) {
    this.axiosInstance = axiosInstance;

    let encryptionCache: Promise<AesGcmEncryptionService> | undefined;
    const getEnc = async (): Promise<AesGcmEncryptionService | undefined> => {
      if (!this.encryption) return undefined;
      if (typeof this.encryption === "function") {
        encryptionCache ??= this.encryption(); // lazy, cached
        return encryptionCache;
      }
      return this.encryption;
    };

    this.axiosInstance.interceptors.request.use(async (config) => {
      const enc = await getEnc();
      if (!enc) return config;

      const method = (config.method || "get").toUpperCase();
      if (!(method === "POST" || method === "PUT" || method === "PATCH"))
        return config;

      const ctHeader =
        (
          config.headers?.["Content-Type"] as string | undefined
        )?.toLowerCase() ??
        (config.headers?.["content-type"] as string | undefined)?.toLowerCase();
      const isJson = !ctHeader || ctHeader.includes("application/json");
      if (!isJson) return config;

      let data: any = config.data;
      if (data == null) return config;

      // Skip non-JSON bodies
      if (typeof FormData !== "undefined" && data instanceof FormData)
        return config;
      if (typeof Blob !== "undefined" && data instanceof Blob) return config;
      if (typeof ArrayBuffer !== "undefined" && data instanceof ArrayBuffer)
        return config;

      const aad = `${method}:${canonicalizePath(config.url || "")}`;

      // Normalize body to object/array
      if (typeof data === "string") {
        const s = data.trim();
        if (s.startsWith("{") || s.startsWith("[")) {
          try {
            data = JSON.parse(s);
          } catch {
            /* leave as is */
          }
        }
      } else if (typeof data === "object" && !Array.isArray(data)) {
        try {
          // @ts-ignore optional toJson pattern
          const maybe = data.toJson?.();
          if (maybe && typeof maybe === "object") data = maybe;
        } catch {
          /* ignore */
        }
      }

      if (Array.isArray(data)) {
        config.data = await enc.encryptJson({ payload: data }, { aad });
      } else if (data && typeof data === "object") {
        config.data = await enc.encryptJson(data as Record<string, unknown>, {
          aad,
        });
      } else {
        return config;
      }

      // mark & force JSON
      config.headers = config.headers ?? {};
      (config.headers as any)["X-Encrypted"] = "1";
      (config.headers as any)["Content-Type"] =
        "application/json; charset=utf-8";
      return config;
    });
  }

  public async request<T>(
    endpoint: ApiEndpoint,
    requestData?: any,
    config?: AxiosRequestConfig
  ): Promise<Response<T>> {
    const { url, method } = endpoint;
    try {
      const response = await this.axiosInstance.request<T>({
        url,
        method,
        responseType: config?.responseType,
        data: requestData,
      });
      return { isSuccessful: true, body: response.data };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          return {
            isSuccessful: false,
            errorBody: {
              statusCode: error.response.status,
              message: error.response.data.message,
            },
          };
        }
        if (error.request) {
          return {
            isSuccessful: false,
            errorBody: {
              statusCode: error.request.status,
              message: error.message,
            },
          };
        }
        return {
          isSuccessful: false,
          errorBody: { statusCode: 0, message: error.message },
        };
      }
      return {
        isSuccessful: false,
        errorBody: { statusCode: 300, message: "Unexpected" },
      };
    }
  }

  public async get<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<Response<T>> {
    return this.request<T>({ url, method: "get" }, undefined, config);
  }

  public async post<T>(
    url: string,
    data: any,
    config?: AxiosRequestConfig
  ): Promise<Response<T>> {
    return this.request<T>({ url, method: "post" }, data, config);
  }

  public async put<T>(url: string, data: any): Promise<Response<T>> {
    return this.request<T>({ url, method: "put" }, data);
  }

  public async delete<T>(url: string): Promise<Response<T>> {
    return this.request<T>({ url, method: "delete" });
  }
}
